// Authentication service implementation

import { 
  AuthSession, 
  LoginCredentials, 
  RegistrationData,
  AuthStatus 
} from '@/types/auth';
import { User, Company, UserRole, ROLE_PERMISSIONS, DEFAULT_COMPANY_SETTINGS, DEFAULT_USER_PREFERENCES } from '@/types/user';

class AuthService {
  private static instance: AuthService;
  private session: AuthSession | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  private constructor() {
    console.log('AuthService: Constructor called');
    // Initialize demo data first
    this.initializeDemoData();
    // Load existing session on initialization
    this.loadStoredSession();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Session management
  private loadStoredSession(): void {
    try {
      console.log('AuthService: Loading stored session from localStorage...');
      const storedSession = localStorage.getItem('biurostock_auth_session');
      console.log('AuthService: Raw stored session:', storedSession);
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        console.log('AuthService: Parsed session:', session);
        
        // Convert date strings back to Date objects
        session.expiresAt = new Date(session.expiresAt);
        session.issuedAt = new Date(session.issuedAt);
        
        // Convert user dates
        if (session.user?.createdAt) {
          session.user.createdAt = new Date(session.user.createdAt);
        }
        if (session.user?.lastLoginAt) {
          session.user.lastLoginAt = new Date(session.user.lastLoginAt);
        }
        
        // Convert company dates
        if (session.company?.createdAt) {
          session.company.createdAt = new Date(session.company.createdAt);
        }
        if (session.company?.updatedAt) {
          session.company.updatedAt = new Date(session.company.updatedAt);
        }
        
        console.log('AuthService: Session after date conversion:', session);
        console.log('AuthService: Current time:', new Date());
        console.log('AuthService: Session expires at:', session.expiresAt);
        console.log('AuthService: Session valid?', this.isSessionValid(session));
        
        if (this.isSessionValid(session)) {
          this.session = session;
          console.log('AuthService: Session loaded successfully');
          this.scheduleTokenRefresh();
        } else {
          console.log('AuthService: Session invalid, clearing...');
          this.clearStoredSession();
        }
      } else {
        console.log('AuthService: No stored session found');
      }
    } catch (error) {
      console.error('Error loading stored session:', error);
      this.clearStoredSession();
    }
  }

  private saveSession(session: AuthSession): void {
    this.session = session;
    localStorage.setItem('biurostock_auth_session', JSON.stringify(session));
    this.scheduleTokenRefresh();
  }

  private clearStoredSession(): void {
    this.session = null;
    localStorage.removeItem('biurostock_auth_session');
    localStorage.removeItem('biurostock_refresh_token');
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private scheduleTokenRefresh(): void {
    if (!this.session) return;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh token 10 minutes before expiration
    const refreshTime = this.session.expiresAt.getTime() - Date.now() - (10 * 60 * 1000);
    
    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    }
  }

  // Authentication methods
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    try {
      console.log('AuthService: Starting login for:', credentials.email);
      
      // Mock authentication - validate against localStorage users
      const users = this.getStoredUsers();
      console.log('AuthService: Found users in localStorage:', users);
      
      const user = users.find(u => 
        u.email === credentials.email && 
        u.status === AuthStatus.ACTIVE
      );

      console.log('AuthService: Found matching user:', user);

      if (!user) {
        throw new Error('Invalid email or password');
      }

      // In a real app, we would verify the password hash
      // For mock purposes, we'll use a simple check
      const mockPassword = 'password123'; // Default password for demo
      if (credentials.password !== mockPassword) {
        console.log('AuthService: Password mismatch');
        throw new Error('Invalid email or password');
      }

      console.log('AuthService: Password OK, checking 2FA...');
      if (user.twoFactorEnabled && !credentials.twoFactorCode) {
        throw new Error('Two-factor authentication code required');
      }

      console.log('AuthService: 2FA check passed');

      // Get user's company
      const company = this.getCompanyById(user.companyId);
      if (!company) {
        throw new Error('Company not found');
      }

      // Create session
      const session: AuthSession = {
        token: this.generateToken(),
        refreshToken: this.generateRefreshToken(),
        user: {
          ...user,
          passwordHash: undefined // Remove password hash from session
        },
        company,
        expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000), // 8 hours
        issuedAt: new Date()
      };

      console.log('AuthService: Created session:', session);

      // Update last login
      user.lastLoginAt = new Date();
      this.updateUser(user);

      this.saveSession(session);
      console.log('AuthService: Session saved to localStorage');
      
      return session;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Login failed');
    }
  }

  async register(data: RegistrationData): Promise<{ user: User; company: Company }> {
    try {
      // Validate NIP (mock validation)
      if (!this.validateNIP(data.nip)) {
        throw new Error('Invalid NIP number');
      }

      // Check if email already exists
      if (this.emailExists(data.email)) {
        throw new Error('Email already registered');
      }

      // Create company
      const company: Company = {
        id: this.generateId(),
        name: data.companyName,
        nip: data.nip,
        address: data.address,
        industry: data.industry,
        plan: 'free',
        settings: DEFAULT_COMPANY_SETTINGS,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Create admin user
      const user: User = {
        id: this.generateId(),
        companyId: company.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: UserRole.ADMIN,
        passwordHash: this.hashPassword(data.password),
        emailVerified: false,
        status: AuthStatus.PENDING_VERIFICATION,
        preferences: DEFAULT_USER_PREFERENCES,
        permissions: ROLE_PERMISSIONS[UserRole.ADMIN],
        createdAt: new Date(),
        lastLoginAt: undefined
      };

      // Save to localStorage
      this.saveCompany(company);
      this.saveUser(user);

      // Send verification email (mock)
      await this.sendVerificationEmail(user.email);

      return { user, company };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Registration failed');
    }
  }

  async logout(): Promise<void> {
    this.clearStoredSession();
  }

  async refreshToken(): Promise<AuthSession> {
    if (!this.session) {
      throw new Error('No active session');
    }

    // Create new session with extended expiration
    const newSession: AuthSession = {
      ...this.session,
      token: this.generateToken(),
      expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000),
      issuedAt: new Date()
    };

    this.saveSession(newSession);
    return newSession;
  }

  // Session validation
  isSessionValid(session?: AuthSession): boolean {
    const sessionToCheck = session || this.session;
    if (!sessionToCheck) return false;
    return new Date() < sessionToCheck.expiresAt;
  }

  isAuthenticated(): boolean {
    return this.isSessionValid();
  }

  getCurrentUser(): User | null {
    return this.session?.user || null;
  }

  getCurrentSession(): AuthSession | null {
    console.log('AuthService: getCurrentSession called, current session:', this.session);
    return this.session;
  }

  // Initialize demo data
  private initializeDemoData(): void {
    console.log('AuthService: Initializing demo data...');
    
    // For debugging, always clear and reinitialize
    console.log('AuthService: Clearing existing demo data for debugging...');
    localStorage.removeItem('biurostock_companies');
    localStorage.removeItem('biurostock_users');
    localStorage.removeItem('biurostock_auth_session');

    console.log('AuthService: Creating demo data...');

    // Create demo company
    const demoCompany = {
      id: 'demo_company_1',
      name: 'Demo Firma Sp. z o.o.',
      nip: '1234567890',
      address: 'ul. Przykładowa 123, 00-001 Warszawa',
      industry: 'Usługi IT',
      plan: 'pro',
      settings: {
        monthlyBudget: 10000,
        budgetAlert80: true,
        budgetAlert90: true,
        budgetAlert100: true,
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        autoOrderEnabled: true,
        currency: 'PLN'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Create demo users
    const demoUsers = [
      {
        id: 'demo_user_admin',
        companyId: 'demo_company_1',
        email: 'admin@demo.pl',
        firstName: 'Jan',
        lastName: 'Kowalski',
        role: 'admin',
        emailVerified: true,
        status: AuthStatus.ACTIVE,
        preferences: {
          theme: 'light',
          language: 'pl',
          defaultView: 'dashboard',
          notificationSettings: {
            email: true,
            push: true,
            sms: false,
            lowStockAlerts: true,
            budgetAlerts: true
          }
        },
        permissions: {
          canOrder: true,
          canEditStock: true,
          canViewReports: true,
          canManageUsers: true,
          canChangeSettings: true,
          canApproveOrders: true
        },
        createdAt: new Date(),
        lastLoginAt: null
      },
      {
        id: 'demo_user_manager',
        companyId: 'demo_company_1',
        email: 'manager@demo.pl',
        firstName: 'Anna',
        lastName: 'Nowak',
        role: 'ordering_manager',
        emailVerified: true,
        status: AuthStatus.ACTIVE,
        preferences: {
          theme: 'light',
          language: 'pl',
          defaultView: 'products',
          notificationSettings: {
            email: true,
            push: true,
            sms: false,
            lowStockAlerts: true,
            budgetAlerts: true
          }
        },
        permissions: {
          canOrder: true,
          canEditStock: true,
          canViewReports: true,
          canManageUsers: false,
          canChangeSettings: false,
          canApproveOrders: true
        },
        createdAt: new Date(),
        lastLoginAt: null
      }
    ];

    // Save demo data
    localStorage.setItem('biurostock_companies', JSON.stringify([demoCompany]));
    localStorage.setItem('biurostock_users', JSON.stringify(demoUsers));
    
    console.log('AuthService: Demo data saved');
    console.log('AuthService: Demo users:', demoUsers);
  }

  // Helper methods
  private generateId(): string {
    return 'bs_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  private generateToken(): string {
    return btoa(JSON.stringify({
      id: this.generateId(),
      timestamp: Date.now(),
      random: Math.random()
    }));
  }

  private generateRefreshToken(): string {
    return btoa(JSON.stringify({
      id: this.generateId(),
      type: 'refresh',
      timestamp: Date.now(),
      random: Math.random()
    }));
  }

  private hashPassword(password: string): string {
    // In a real app, use bcrypt or similar
    // For mock purposes, just return a base64 encoded version
    return btoa(password + 'salt');
  }

  private validateNIP(nip: string): boolean {
    // Basic NIP validation (Polish tax ID)
    const nipRegex = /^\d{10}$/;
    return nipRegex.test(nip);
  }

  private emailExists(email: string): boolean {
    const users = this.getStoredUsers();
    return users.some(user => user.email === email);
  }

  private async sendVerificationEmail(email: string): Promise<void> {
    // Mock email sending
    console.log(`Verification email sent to: ${email}`);
    // In a real app, this would send an actual email
  }

  // Data management
  private getStoredUsers(): User[] {
    try {
      const users = localStorage.getItem('biurostock_users');
      console.log('AuthService: getStoredUsers - raw data:', users);
      const parsed = users ? JSON.parse(users) : [];
      console.log('AuthService: getStoredUsers - parsed:', parsed);
      return parsed;
    } catch (error) {
      console.error('AuthService: Error parsing stored users:', error);
      return [];
    }
  }

  private saveUser(user: User): void {
    const users = this.getStoredUsers();
    const existingIndex = users.findIndex(u => u.id === user.id);
    
    if (existingIndex >= 0) {
      users[existingIndex] = user;
    } else {
      users.push(user);
    }
    
    localStorage.setItem('biurostock_users', JSON.stringify(users));
  }

  private updateUser(user: User): void {
    this.saveUser(user);
  }

  private getCompanyById(id: string): Company | null {
    try {
      const companies = localStorage.getItem('biurostock_companies');
      if (!companies) return null;
      
      const companyList: Company[] = JSON.parse(companies);
      return companyList.find(c => c.id === id) || null;
    } catch {
      return null;
    }
  }

  private saveCompany(company: Company): void {
    try {
      const companies = localStorage.getItem('biurostock_companies');
      const companyList: Company[] = companies ? JSON.parse(companies) : [];
      
      const existingIndex = companyList.findIndex(c => c.id === company.id);
      if (existingIndex >= 0) {
        companyList[existingIndex] = company;
      } else {
        companyList.push(company);
      }
      
      localStorage.setItem('biurostock_companies', JSON.stringify(companyList));
    } catch (error) {
      console.error('Error saving company:', error);
    }
  }
}

export default AuthService.getInstance();